"use client";

/**
 * AnalysisJobDetailsClient — loads and renders a single completed analysis.
 * Port of reactjs/src/pages/analyse/AnalysisJobDetails.tsx
 */
import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import { onAuthStateChanged } from "firebase/auth";
import { getFirebaseAuth } from "@/platform/firebase/auth";
import { getAnalysis } from "../api";
import type { AnalysisState } from "../types";
import { initialAnalysisState } from "../types";
import { PartialResults } from "./PartialResults";
import { FullAnalysisResults } from "./FullAnalysisResults";
import { SenseiMarkLoader } from "@/shared/components/layout/SenseiMarkLoader";

interface Props {
  jobId: string;
}

export function AnalysisJobDetailsClient({ jobId }: Props) {
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(true);
  const [analysisTitle, setAnalysisTitle] = useState("Analysis Report");
  const [analysisType, setAnalysisType] = useState("");
  const [createdAt, setCreatedAt] = useState("");
  const [analysisState, setAnalysisState] = useState<AnalysisState>(initialAnalysisState);

  useEffect(() => {
    if (!jobId) {
      setAnalysisState((prev) => ({ ...prev, error: "No job ID provided" }));
      setIsLoading(false);
      return;
    }

    const auth = getFirebaseAuth();
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setAnalysisState((prev) => ({ ...prev, error: "Please log in to view this analysis" }));
        setIsLoading(false);
        return;
      }

      setIsLoading(true);

      try {
        const data = await getAnalysis(jobId);

        if (data.title) setAnalysisTitle(data.title);
        if (data.type) setAnalysisType(data.type);
        if (data.created_at) setCreatedAt(new Date(data.created_at).toLocaleString());

        setAnalysisState({
          isProcessing: false,
          isUploading: false,
          progress: 100,
          progressMessage: "Analysis complete",
          partialResults: null,
          results: data,
          error: null,
        });
      } catch (err) {
        console.error("Failed to load analysis:", err);
        setAnalysisState((prev) => ({
          ...prev,
          error: err instanceof Error ? err.message : "Failed to load analysis",
        }));
      } finally {
        setIsLoading(false);
      }
    });

    return () => unsubscribe();
  }, [jobId]);

  if (isLoading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "60vh" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ margin: "0 auto 16px", width: "fit-content" }}>
            <SenseiMarkLoader size={40} />
          </div>
          <p style={{ margin: 0, fontSize: 14, color: "var(--ink-soft)" }}>Loading analysis...</p>
        </div>
      </div>
    );
  }

  if (analysisState.error) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "60vh" }}>
        <div style={{ textAlign: "center", maxWidth: 400 }}>
          <div style={{ fontSize: 40, marginBottom: 16 }}>⚠️</div>
          <p style={{ margin: "0 0 8px", fontSize: 16, fontWeight: 600 }}>Failed to Load Analysis</p>
          <p style={{ margin: "0 0 20px", fontSize: 14, color: "var(--ink-soft)" }}>{analysisState.error}</p>
          <button
            onClick={() => router.push("/analyse?type=any")}
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              padding: "10px 20px", borderRadius: 10,
              border: "1px solid var(--line-soft)", background: "none",
              cursor: "pointer", fontSize: 14,
            }}
          >
            ← Back to Analyses
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto", padding: "32px 24px" }}>
      {/* Back nav */}
      <div style={{ marginBottom: 24 }}>
        <button
          onClick={() => router.push("/analyse?type=any")}
          style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            padding: "7px 14px", borderRadius: 8,
            border: "none", background: "none",
            cursor: "pointer", fontSize: 13, color: "var(--ink-soft)",
            transition: "all 0.15s",
          }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = "var(--paper-1)"; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = "none"; }}
        >
          ← Back to Analyses
        </button>
      </div>

      {/* Title */}
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ margin: "0 0 8px", fontSize: 28, fontWeight: 700 }}>{analysisTitle}</h1>
        <p style={{ margin: 0, fontSize: 14, color: "var(--ink-soft)" }}>
          {analysisType && <span style={{ textTransform: "capitalize" }}>{analysisType}</span>}
          {analysisType && createdAt && <span> · </span>}
          {createdAt && <span>{createdAt}</span>}
        </p>
      </div>

      {/* Content */}
      <PartialResults analysisState={analysisState} />
      <FullAnalysisResults analysisState={analysisState} jobId={jobId} />
    </div>
  );
}
